import { useMutation, useQuery } from '@tanstack/react-query';

import { LearningObjectTypes } from 'shared/types/LearningObjectType';

import { getSnapshotForAssignment } from './assignmentApi';
import {
  getLatestLearningObjectSnapshot,
  getLearningObjectSnapshotById,
  listLearningObjectSnapshots,
  restoreLearningObjectSnapshot
} from '@/queries/learningObjectSnapshotApi';

export type LearningObjectSnapshot = {
  _id: string;
  createdAt: string;
  createdBy: string;
  data: string;
  documentVersion?: number;
  isValid: boolean;
  language: string;
  learningObjectDraft: string;
  type: LearningObjectTypes;
  metadata?: {
    description?: string;
    thumbnailUrl?: string | null;
  };
  publishedAt: Date;
  themeId?: string;
  title: string;
  updatedAt: string;
  version: number;
};

export const useFetchLatestSnapshotByDraftId = ({
  enabled,
  learningObjectDraftId,
  publishedOnly
}: {
  enabled?: boolean;
  learningObjectDraftId?: string;
  publishedOnly?: boolean;
}) => {
  return useQuery<LearningObjectSnapshot>({
    queryKey: [
      'snapshot',
      'latest',
      {
        learningObjectDraftId,
        ...(publishedOnly && { publishedOnly })
      }
    ],
    queryFn: () =>
      getLatestLearningObjectSnapshot({
        learningObjectDraftId: learningObjectDraftId as string,
        publishedOnly
      }),
    enabled: !!learningObjectDraftId && enabled !== false
  });
};

export const useFetchSnapshotById = ({
  enabled = true,
  learningObjectSnapshotId
}: {
  enabled?: boolean;
  learningObjectSnapshotId?: string;
}) => {
  return useQuery<LearningObjectSnapshot>({
    queryKey: [
      'snapshot',
      {
        learningObjectSnapshotId
      }
    ],
    queryFn: () => getLearningObjectSnapshotById({ learningObjectSnapshotId }),
    enabled: !!learningObjectSnapshotId && enabled
  });
};

export const useFetchAllSnapshots = (learningObjectDraftId?: string) => {
  return useQuery<LearningObjectSnapshot[]>({
    queryKey: ['snapshots', 'list', { learningObjectDraftId }],
    queryFn: () =>
      listLearningObjectSnapshots({
        learningObjectDraftId: learningObjectDraftId as string
      }),
    enabled: !!learningObjectDraftId
  });
};

export const useRestoreSnapshot = ({ onError, onSuccess }: { onError: () => void; onSuccess: () => void }) => {
  return useMutation({
    mutationFn: (variables: { learningObjectSnapshotId?: string }) => restoreLearningObjectSnapshot(variables),
    onError,
    onSuccess
  });
};

export function useFetchSnapshotForAssignment({ assignmentId, enabled }: { assignmentId?: string; enabled?: boolean }) {
  return useQuery<LearningObjectSnapshot>({
    queryKey: ['snapshot', { assignmentId }],
    queryFn: () => getSnapshotForAssignment({ assignmentId: assignmentId as string }),
    enabled: !!assignmentId || enabled !== false
  });
}
